// contact_form.js
import { Component } from "react";
import "../styles/contact_form.css";

class ContactForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // One state value for each form field
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      company: "",
      job_title: "",
      inquiry_type: "",
      find_how: "",
      message: "",

      // UI state
      errorMessage: "", // Shows in red when something goes wrong
      successMessage: "", // Shows in green when form submits successfully
      isLoading: false, // Disables button while waiting for server response
    };
  }

  // ==========================================
  // HANDLE INPUT CHANGES
  // Every input field calls this same function.
  // [e.target.name] dynamically updates whichever
  // field the user is currently typing in.
  // Also clears any error/success messages
  // as soon as the user starts correcting their input.
  // ==========================================
  handleChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
      errorMessage: "",
      successMessage: "",
    });
  };

  // ==========================================
  // HANDLE FORM SUBMISSION
  // Step 1: Validate on the frontend
  // Step 2: Send to Flask backend if valid
  // Step 3: Handle success or error response
  // ==========================================
  handleSubmit = async (e) => {
    e.preventDefault(); // Stops browser from refreshing the page on submit

    const {
      first_name,
      last_name,
      email,
      phone,
      company,
      job_title,
      inquiry_type,
      find_how,
      message,
    } = this.state;

    // ------------------------------------------
    // FRONTEND VALIDATION
    // These checks happen instantly in the browser
    // before anything is sent to the server.
    // Saves time and reduces unnecessary API calls.
    // ------------------------------------------

    // Check 1: Required fields must not be empty
    if (
      !first_name.trim() ||
      !last_name.trim() ||
      !email.trim() ||
      !inquiry_type ||
      !message.trim()
    ) {
      this.setState({
        errorMessage: "Please fill in your name, email and message.",
      });
      return; // Stop here — do not send to backend
    }

    // Check 2: Email must be a valid format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.setState({
        errorMessage: "Please enter a valid email address.",
      });
      return;
    }

    // Check 3: Message must have meaningful content
    if (message.trim().length < 10) {
      this.setState({
        errorMessage: "Please write a little more in your message.",
      });
      return;
    }

    // ------------------------------------------
    // ALL CHECKS PASSED
    // Show loading state so user knows something
    // is happening after they click Submit.
    // ------------------------------------------
    this.setState({ isLoading: true });

    try {
      // fetch() sends an HTTP request to your Flask server.
      // async/await means React waits for the response
      // before moving to the next line.
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/contact`,
        {
          method: "POST", // POST because we are sending data to be saved

          headers: {
            // Tells Flask the data is formatted as JSON.
            // Without this header Flask cannot read the body
            // and request.get_json() returns None.
            "Content-Type": "application/json",
          },

          // Convert JS object to JSON string for sending over internet.
          // Field names here must exactly match what Flask expects —
          // see controllers/contact_controllers.py data.get("first_name") etc.
          body: JSON.stringify({
            first_name,
            last_name,
            email,
            phone,
            company,
            job_title,
            inquiry_type,
            find_how,
            message,
          }),
        },
      );

      // Read the JSON response Flask sent back.
      // Flask returns either:
      // { "success": true,  "message": "Your message has been sent..." }
      // { "success": false, "message": "Please fill in all required fields." }
      const data = await response.json();

      if (data.success) {
        // SUCCESS
        // Form saved to database, emails sent to XLP and to user.
        // Clear all form fields and show success message.
        this.setState({
          successMessage:
            "Thank you! Your message has been sent. We will be in touch shortly.",
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          company: "",
          job_title: "",
          inquiry_type: "",
          find_how: "",
          message: "",
        });
      } else {
        //  BACKEND ERROR
        // Flask returned success: false with a reason.
        // Show that reason directly to the user.
        this.setState({ errorMessage: data.message });
      }
    } catch (error) {
      //  NETWORK ERROR
      // Could not reach Flask at all — server not running,
      // wrong URL, internet issue, CORS problem.
      this.setState({
        errorMessage:
          "Could not reach the server. Please try again in a moment.",
      });
    } finally {
      // Always runs whether success or failure.
      // Turns off loading state so button becomes clickable again.
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { isLoading, errorMessage, successMessage } = this.state;

    return (
      <>
        <section className="py-5" id="contact">
          <div className="container py-lg-5" style={{ maxWidth: "1400px" }}>
            <div className="row mb-5">
              <div className="col-lg-8">
                <h2 className="display-5 text-dark mb-3 fw-medium lh-sm">
                  {this.props.title || (
                    <>
                      Ready to move your school
                      <br />
                      forward? Let's talk.
                    </>
                  )}
                </h2>

                <p
                  className="text-secondary"
                  style={{
                    fontSize: "16px",
                    lineHeight: 1.6,
                    maxWidth: "700px",
                  }}
                >
                  {this.props.description || (
                    <>
                      Whether you are seeking{" "}
                      <strong>clarity, growth, or transformation</strong> for
                      your school, we are here to help. Reach out to start the
                      conversation—no pressure, no obligation.
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="card border-0 rounded-4 overflow-hidden contact-shadow">
              <div className="row g-0">
                {/* ---- LEFT PANEL ---- */}
                <div className="col-lg-5 d-flex align-items-start p-4 p-lg-5 contact-left-bg">
                  <div className="bg-white p-4 p-lg-5 rounded-3 shadow-sm w-100 mt-2">
                    <h4 className="h4 text-dark fw-bold mb-3">
                      Let's Connect!
                    </h4>
                    <p
                      className="text-secondary small mb-4"
                      style={{ lineHeight: 1.5 }}
                    >
                      Fill out the form, and let's talk about how we can support
                      your business with tailored solutions.
                    </p>

                    <div className="d-flex align-items-center p-3 rounded-2 mail-box-bg">
                      <span className="fs-4 me-3 text-brand">📧</span>
                      <div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "12px" }}
                        >
                          Mail us at:
                        </div>
                        <a
                          href="mailto:info@xlp.com"
                          className="text-dark fw-medium text-decoration-none"
                          style={{ fontSize: "14px" }}
                        >
                          info@xlp.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---- RIGHT PANEL — THE FORM ---- */}
                <div className="col-lg-7 p-4 p-lg-5 bg-white">
                  <div className="d-flex justify-content-between align-items-center mb-4 pb-4 border-bottom">
                    <h3 className="h4 mb-0 fw-medium text-brand">
                      Book a free consultation
                    </h3>
                  </div>

                  {/* ERROR MESSAGE — shows when something goes wrong */}
                  {errorMessage && (
                    <div
                      className="alert alert-danger py-2 mb-4"
                      style={{ fontSize: "14px" }}
                      role="alert"
                    >
                      {errorMessage}
                    </div>
                  )}

                  {/* SUCCESS MESSAGE — shows after successful submission */}
                  {successMessage && (
                    <div
                      className="alert alert-success py-2 mb-4"
                      style={{ fontSize: "14px" }}
                      role="alert"
                    >
                      {successMessage}
                    </div>
                  )}

                  <form onSubmit={this.handleSubmit}>
                    <div className="row g-4 mb-4">
                      {/* FIRST NAME */}
                      <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          className="form-control bg-light xlp-input"
                          placeholder="First Name"
                          value={this.state.first_name}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>

                      {/* LAST NAME */}
                      <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          className="form-control bg-light xlp-input"
                          placeholder="Last Name"
                          value={this.state.last_name}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>

                      {/* EMAIL */}
                      <div className="col-12">
                        <label className="form-label small fw-medium text-dark">
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          className="form-control bg-light xlp-input"
                          placeholder="janeDoe@example.com"
                          value={this.state.email}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>

                      {/* PHONE — optional */}
                      <div className="col-12">
                        <label className="form-label small fw-medium text-dark">
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control bg-light xlp-input"
                          placeholder="+123 456 789"
                          value={this.state.phone}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>

                      {/* COMPANY — optional */}
                      <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark">
                          Company Name
                        </label>
                        <input
                          type="text"
                          name="company"
                          className="form-control bg-light xlp-input"
                          placeholder="Company Name"
                          value={this.state.company}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>

                      {/* JOB TITLE — optional */}
                      <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark">
                          Job Title
                        </label>
                        <input
                          type="text"
                          name="job_title"
                          className="form-control bg-light xlp-input"
                          placeholder="Job Title"
                          value={this.state.job_title}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>

                      {/* INQUIRY TYPE DROPDOWN */}
                      <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark">
                          What are you interested in?{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <select
                          name="inquiry_type"
                          className="form-select bg-light xlp-input"
                          value={this.state.inquiry_type}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        >
                          <option value="">Select an option</option>
                          <option value="schools">
                            Schools & Institutions
                          </option>
                          <option value="students">Individual Learning</option>
                          <option value="channel">Channel Partnership</option>
                          <option value="workshops">XLP Workshops</option>
                          <option value="products">Robotics Products</option>
                          <option value="support">Support</option>
                          <option value="general">General Inquiry</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      {/* FIND US HOW TYPE DROPDOWN */}
                      <div className="col-md-6">
                        <label className="form-label small fw-medium text-dark">
                          How did you find us?{" "}
                          {/* <span className="text-danger">*</span> */}
                        </label>
                        <select
                          name="find_how"
                          className="form-select bg-light xlp-input"
                          value={this.state.find_how}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        >
                          <option value="">Select an option</option>
                          <option value="youtube">YouTube</option>
                          <option value="instagram">Instagram</option>
                          <option value="search-engine">
                            Google / Search Engine
                          </option>
                          <option value="referral">Friend or Referral</option>
                          <option value="institution">School or College</option>
                          <option value="workshop-event">
                            Workshop or Event
                          </option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="social-media">Social Media</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      {/* MESSAGE */}
                      <div className="col-12">
                        <label className="form-label small fw-medium text-dark">
                          Message <span className="text-danger">*</span>
                        </label>
                        <textarea
                          name="message"
                          className="form-control bg-light xlp-input"
                          rows="4"
                          placeholder="To better assist you, please describe how we can help :)"
                          value={this.state.message}
                          onChange={this.handleChange}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    {/* SUBMIT BUTTON
                        - Changes text while loading
                        - Disabled while loading to prevent double submissions */}
                    <button
                      type="submit"
                      className="btn btn-brand px-5 py-3 mt-2 fw-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? "Sending..." : "Submit"}
                    </button>

                    <p
                      className="text-muted mt-4 pt-4 border-top"
                      style={{ fontSize: "12px", lineHeight: 1.6 }}
                    >
                      By submitting this form you agree to our Privacy Policy.
                      XLP may contact you via email or phone for scheduling or
                      marketing purposes.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}

export default ContactForm;
